import { create } from 'zustand';
import { parseJwt } from '@/lib/jwt.util';

const GRACE_KEY = 'agrovet_mfa_grace';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  permissions: Set<string>;
  mfaGracePeriodEndsAt: Date | null;
  isAuthenticated: boolean;
  setTokens: (
    accessToken: string,
    refreshToken: string,
    expiresAt: Date,
    mfaGracePeriodEndsAt?: string | null,
  ) => void;
  setUser: (user: AuthUser, permissions: string[]) => void;
  clearMfaGracePeriod: () => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  refreshToken: localStorage.getItem('agrovet_refresh_token'),
  user: null,
  permissions: new Set<string>(),
  mfaGracePeriodEndsAt: (() => {
    const v = localStorage.getItem(GRACE_KEY);
    return v ? new Date(v) : null;
  })(),
  isAuthenticated: false,

  setTokens: (accessToken, refreshToken, _expiresAt, mfaGracePeriodEndsAt) => {
    const claims = parseJwt(accessToken);
    localStorage.setItem('agrovet_refresh_token', refreshToken);

    const gracePeriodUpdate: Partial<AuthState> = {};
    if (mfaGracePeriodEndsAt !== undefined) {
      if (mfaGracePeriodEndsAt) {
        localStorage.setItem(GRACE_KEY, mfaGracePeriodEndsAt);
        gracePeriodUpdate.mfaGracePeriodEndsAt = new Date(mfaGracePeriodEndsAt);
      } else {
        localStorage.removeItem(GRACE_KEY);
        gracePeriodUpdate.mfaGracePeriodEndsAt = null;
      }
    }

    set({
      accessToken,
      refreshToken,
      isAuthenticated: true,
      user: {
        id: (claims['sub'] as string) ?? '',
        email: (claims['email'] as string) ?? '',
        fullName: (claims['name'] as string) ?? '',
        role: (claims['role'] as string) ?? '',
      },
      ...gracePeriodUpdate,
    });
  },

  setUser: (user, permissions) => {
    set({ user, permissions: new Set(permissions) });
  },

  clearMfaGracePeriod: () => {
    localStorage.removeItem(GRACE_KEY);
    set({ mfaGracePeriodEndsAt: null });
  },

  clearAuth: () => {
    localStorage.removeItem('agrovet_refresh_token');
    localStorage.removeItem(GRACE_KEY);
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      permissions: new Set<string>(),
      mfaGracePeriodEndsAt: null,
      isAuthenticated: false,
    });
  },
}));
